import React from "react";
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  useTheme,
  Typography,
  Pagination,
} from "@mui/material";
import ClickTooltip from "../ClickTooltip";
type GenericListProps<T> = {
  items: T[];
  getKey: (item: T) => string | number;
  getTitle: (item: T) => string;
  getSubtitle?: (item: T) => string;
  renderIcon?: (item: T) => React.ReactNode;
  renderActions?: React.ReactNode | ((item: T) => React.ReactNode);
  onItemClick?: (item: T) => void;
  // Pagination props
  page?: number;
  onPageChange?: (page: number) => void;
  totalCount?: number;
  itemsPerPage?: number;
  showPagination?: boolean;
  // Infinite scroll sentinel rendered at the bottom of the scrollable list
  listFooter?: React.ReactNode;
};

function CustomList<T>({
  items,
  getKey,
  getTitle,
  getSubtitle,
  renderIcon,
  renderActions,
  onItemClick,
  // Pagination props with defaults
  page = 1,
  onPageChange,
  totalCount = 0,
  itemsPerPage = 10,
  showPagination = false,
  listFooter,
}: GenericListProps<T>) {
  const theme = useTheme();

  // Calculate total pages
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    onPageChange?.(value);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        minHeight: 0, // Important for proper flexbox sizing
      }}
    >
      {/* List Container - should grow and shrink */}
      <Box
        sx={{
          flex: "1 1 auto",
          overflow: "hidden", // Contain the list
          display: "flex",
          flexDirection: "column",
        }}
      >
        <List
          sx={{
            flex: "1 1 auto",
            overflowY: "auto",
            width: "100%",
            minWidth: 0,
            py: 0,
          }}
        >
          {items.map((item) => (
            <ListItem
              key={getKey(item)}
              sx={{
                borderRadius: "11px",
                mb: 1,
                py: 0,
                px: 1.5,
                cursor: onItemClick ? "pointer" : "default",
                backgroundColor: theme.palette.common.white,
                border: `1px solid ${theme.palette.divider}`,
                transition: "border-color 150ms, background 150ms",
                "&:hover": {
                  borderColor: theme.palette.border.cyan,
                  backgroundColor: theme.palette.surface.cyanHover,
                },
                display: "flex",
                gap: 1,
                minWidth: 0,
                width: "100%",
              }}
              onClick={() => onItemClick?.(item)}
            >
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  mt: { xs: 1, lg: 0 },
                  flexDirection: { xs: "column", lg: "row" },
                  alignItems: { xs: "flex-start", lg: "center" },
                  justifyContent: { xs: "flex-start", lg: "space-between" },
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1,
                    flex: "1 1 auto",
                    minWidth: 0,
                    alignItems: "center",
                    overflow: "hidden",
                    width: "100%",
                  }}
                >
                  {renderIcon && (
                    <ListItemIcon sx={{ minWidth: 0, flexShrink: 0 }}>
                      {renderIcon(item)}
                    </ListItemIcon>
                  )}

                  <ListItemText
                    sx={{
                      flex: "1 1 auto",
                      minWidth: 0,
                      mr: { xs: 0, lg: 2 },
                      overflow: "hidden",
                    }}
                    primary={
                      <ClickTooltip title={getTitle(item)}>
                        <Typography
                          variant="body1"
                          fontSize="16px"
                          color="text.primary"
                          noWrap
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "block",
                            width: "calc(100% - 20px)",
                            lineHeight: 1.2,
                          }}
                        >
                          {getTitle(item)}
                        </Typography>
                      </ClickTooltip>
                    }
                    secondary={
                      getSubtitle?.(item) && (
                        <ClickTooltip title={getSubtitle(item)}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              display: "block",
                              width: "calc(100% - 20px)",
                              lineHeight: 1.1,
                            }}
                          >
                            {getSubtitle(item)}
                          </Typography>
                        </ClickTooltip>
                      )
                    }
                  />
                </Box>

                {renderActions && (
                  <Box
                    sx={{
                      py: 1,
                      pr: 1,
                      display: "flex",
                      justifyContent: { xs: "flex-start", lg: "flex-end" },
                      alignItems: "center",
                      flexShrink: 0,
                      minWidth: "auto",
                      gap: 0.5,
                      width: { xs: "100%", lg: "auto" },
                    }}
                  >
                    {typeof renderActions === "function"
                      ? renderActions(item)
                      : renderActions}
                  </Box>
                )}
              </Box>
            </ListItem>
          ))}
          {listFooter}
        </List>
      </Box>

      {/* Pagination - fixed height at the bottom */}
      {showPagination && totalPages > 0 && (
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            pt: 1,
            px: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="medium"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
}

export default CustomList;